import {
  CircularProgress,
  createStyles,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft"
import ChevronRightIcon from "@material-ui/icons/ChevronRight"
import {
  Heading,
  NavCollapseControl,
  ViraButton,
  ViraCard,
} from "@ksana/vira-ui"
import clsx from "clsx"
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react"
import { formatDateTime } from "../../../utils/dateTimeUtils"
import { colors } from "../../../utils/theme"
import { TranscriptSessionSummary } from "../../../utils/transcriptApi"
import TranscriptStatusChip from "./TranscriptStatusChip"

export type SessionInputDotStatus =
  | "ready"
  | "missing"
  | "in_progress"
  | "disabled"

export type SessionInputDot = {
  id: string
  label: string
  status: SessionInputDotStatus
  detail: string
}

export type SessionInputSummary = {
  readyCount: number
  totalCount: number
  dots: SessionInputDot[]
}

interface TranscriptSessionListProps {
  sessions: TranscriptSessionSummary[]
  selectedSessionId: string | null
  isLoading: boolean
  onSelectSession: (sessionId: string) => void
  onDeleteSession: (sessionId: string) => void
  /** Per-session Session Status (App / Session / Plan / Note). */
  inputSummaries?: Record<string, SessionInputSummary>
  variant?: "default" | "clientDetail"
  onCreateEmptySession?: () => void
  isCreatingEmptySession?: boolean
  /**
   * Client Detail only: collapse the list into a narrow session menu rail so
   * session detail can use the rest of the page.
   */
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
}

function formatSessionRailLabel(timestamp: string): string {
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
}

/**
 * Renders transcript session summaries, selection state, Session Status, and a
 * overflow menu for destructive actions. In Client Detail, supports a
 * collapsible menu rail.
 */
export default function TranscriptSessionList({
  sessions,
  selectedSessionId,
  isLoading,
  onSelectSession,
  onDeleteSession,
  inputSummaries = {},
  variant = "default",
  onCreateEmptySession,
  isCreatingEmptySession = false,
  collapsed = false,
  onCollapsedChange,
}: TranscriptSessionListProps) {
  const classes = useStyles()
  const isClientDetail = variant === "clientDetail"
  const showCollapse = isClientDetail && typeof onCollapsedChange === "function"
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null)
  const railListRef = useRef<HTMLDivElement | null>(null)
  const [railCanScrollLeft, setRailCanScrollLeft] = useState(false)
  const [railCanScrollRight, setRailCanScrollRight] = useState(false)

  const updateRailScrollState = useCallback(() => {
    const el = railListRef.current
    if (!el) {
      setRailCanScrollLeft(false)
      setRailCanScrollRight(false)
      return
    }
    const maxScroll = el.scrollWidth - el.clientWidth
    setRailCanScrollLeft(el.scrollLeft > 2)
    setRailCanScrollRight(maxScroll - el.scrollLeft > 2)
  }, [])

  const scrollRailByPage = useCallback((direction: -1 | 1) => {
    const el = railListRef.current
    if (!el) return
    const amount = Math.max(Math.round(el.clientWidth * 0.8), 96)
    el.scrollBy({ left: direction * amount, behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (!collapsed) {
      return
    }
    updateRailScrollState()
    const el = railListRef.current
    if (!el) {
      return
    }
    el.addEventListener("scroll", updateRailScrollState, { passive: true })
    window.addEventListener("resize", updateRailScrollState)
    return () => {
      el.removeEventListener("scroll", updateRailScrollState)
      window.removeEventListener("resize", updateRailScrollState)
    }
  }, [collapsed, sessions.length, updateRailScrollState])

  useEffect(() => {
    if (!collapsed || !selectedSessionId || !railListRef.current) {
      return
    }
    const selected = railListRef.current.querySelector(
      "[aria-current='true']"
    ) as HTMLElement | null
    selected?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    })
    // Recompute after the smooth scroll settles.
    const timer = window.setTimeout(updateRailScrollState, 220)
    return () => window.clearTimeout(timer)
  }, [collapsed, selectedSessionId, sessions.length, updateRailScrollState])

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuSessionId(null)
  }

  const openMenu = (event: MouseEvent<HTMLElement>, sessionId: string) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuSessionId(sessionId)
  }

  const collapseControl = showCollapse ? (
    <NavCollapseControl
      collapsed={collapsed}
      label={collapsed ? "Expand session list" : "Collapse session list"}
      onClick={() => onCollapsedChange?.(!collapsed)}
    />
  ) : null

  const deleteMenu = (
    <Menu
      id="session-row-menu"
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={closeMenu}
      keepMounted
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem
        onClick={() => {
          if (!menuSessionId) {
            closeMenu()
            return
          }
          const confirmed = window.confirm(
            "Delete this session? Transcripts, scores, and linked plan data for this session will be removed. This cannot be undone."
          )
          const id = menuSessionId
          closeMenu()
          if (confirmed) {
            onDeleteSession(id)
          }
        }}
      >
        <ListItemText
          primary="Delete session…"
          primaryTypographyProps={{
            style: { color: colors.secondary.crimson, fontSize: 14 },
          }}
        />
      </MenuItem>
    </Menu>
  )

  if (isClientDetail && collapsed) {
    const rail = (
      <div className={classes.rail} data-collapsed="true">
        <div className={classes.railToolbar}>
          <div className={classes.railHeader}>{collapseControl}</div>
          {onCreateEmptySession ? (
            <Tooltip title="Create session" placement="bottom">
              <span className={classes.railCreateWrap}>
                <ViraButton
                  variant="secondary"
                  size="sm"
                  className={classes.railCreateButton}
                  onClick={onCreateEmptySession}
                  disabled={isCreatingEmptySession || isLoading}
                  aria-label="Create session"
                >
                  {isCreatingEmptySession ? "…" : "+"}
                </ViraButton>
              </span>
            </Tooltip>
          ) : null}
        </div>
        {isLoading ? (
          <div className={classes.railLoading}>
            <CircularProgress size={18} />
          </div>
        ) : null}
        <div className={classes.railPager}>
          <IconButton
            className={classes.railPageButton}
            size="small"
            aria-label="Scroll sessions left"
            disabled={!railCanScrollLeft}
            onClick={() => scrollRailByPage(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <div
            ref={railListRef}
            className={classes.railList}
            role="group"
            aria-label="Sessions"
            data-loop-scroll=""
          >
            {sessions.map((session) => {
              const selected = session.id === selectedSessionId
              const label = formatSessionRailLabel(session.createdAt)
              const title = session.title || "Session"
              return (
                <Tooltip
                  key={session.id}
                  title={`${title} · ${formatDateTime(session.createdAt)}`}
                  placement="bottom"
                >
                  <button
                    type="button"
                    className={clsx(
                      classes.railItem,
                      selected && classes.railItemSelected
                    )}
                    aria-current={selected ? "true" : undefined}
                    aria-label={`${title} · ${formatDateTime(
                      session.createdAt
                    )}`}
                    onClick={() => onSelectSession(session.id)}
                  >
                    <span className={classes.railItemDate}>{label}</span>
                  </button>
                </Tooltip>
              )
            })}
          </div>
          <IconButton
            className={classes.railPageButton}
            size="small"
            aria-label="Scroll sessions right"
            disabled={!railCanScrollRight}
            onClick={() => scrollRailByPage(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
        {deleteMenu}
      </div>
    )
    return <ViraCard sx={{ maxWidth: "100%" }}>{rail}</ViraCard>
  }

  const body = (
    <>
      <div
        className={clsx(
          classes.listHeader,
          isClientDetail && classes.listHeaderClientDetail
        )}
      >
        <div className={classes.listHeaderTitle}>
          {collapseControl}
          {isClientDetail ? (
            <Heading level="card">Sessions</Heading>
          ) : (
            <Typography variant="h3" className={classes.sectionTitle}>
              Sessions
            </Typography>
          )}
        </div>
        <div className={classes.listHeaderActions}>
          {onCreateEmptySession ? (
            <ViraButton
              variant="secondary"
              onClick={onCreateEmptySession}
              disabled={isCreatingEmptySession || isLoading}
              sx={{ minHeight: 32, fontSize: 12.5, px: 1.5 }}
            >
              {isCreatingEmptySession ? "Creating…" : "Create session"}
            </ViraButton>
          ) : null}
          {isLoading && <CircularProgress size={18} />}
        </div>
      </div>

      {sessions.length === 0 && !isLoading ? (
        <Typography variant="body2" className={classes.emptyState}>
          No transcript sessions are attached to this client yet.
        </Typography>
      ) : (
        <List disablePadding>
          {sessions.map((session, index) => {
            const summary = inputSummaries[session.id]
            const showProcessingChip =
              !summary &&
              session.status !== "complete" &&
              Boolean(session.status)
            const title = session.title || "Session"
            const errorText =
              session.status === "error"
                ? session.errorMessage || "This session couldn’t be processed."
                : null
            const statusDots = summary ? (
              <div
                className={
                  isClientDetail ? classes.statusDots : classes.statusStack
                }
                aria-label={`Session status: ${summary.readyCount} of ${summary.totalCount} ready`}
              >
                {summary.dots.map((dot) =>
                  isClientDetail ? (
                    <Tooltip
                      key={dot.id}
                      title={`${dot.label}: ${dot.detail}`}
                      placement="top"
                      enterDelay={200}
                    >
                      <button
                        type="button"
                        className={clsx(
                          classes.statusDot,
                          classes.statusDotAlone,
                          classes.statusDotButton,
                          dot.status === "ready"
                            ? classes.dot_ready
                            : dot.status === "in_progress"
                            ? classes.dot_in_progress
                            : dot.status === "disabled"
                            ? classes.dot_disabled
                            : classes.dot_missing
                        )}
                        aria-label={`${dot.label}: ${dot.detail}`}
                        onClick={(event) => {
                          // Keep focus/tooltip available without selecting the row.
                          event.stopPropagation()
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <div
                      key={dot.id}
                      className={classes.statusRow}
                      title={dot.detail}
                    >
                      <span
                        className={clsx(
                          classes.statusDot,
                          dot.status === "ready"
                            ? classes.dot_ready
                            : dot.status === "in_progress"
                            ? classes.dot_in_progress
                            : dot.status === "disabled"
                            ? classes.dot_disabled
                            : classes.dot_missing
                        )}
                      />
                      <Typography
                        variant="caption"
                        className={clsx(
                          classes.statusLabel,
                          dot.status === "ready" && classes.statusLabelReady,
                          dot.status === "in_progress" &&
                            classes.statusLabelInProgress
                        )}
                      >
                        {dot.label}
                      </Typography>
                    </div>
                  )
                )}
              </div>
            ) : null
            return (
              <div key={session.id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  selected={session.id === selectedSessionId}
                  onClick={() => onSelectSession(session.id)}
                  className={clsx(
                    classes.sessionListItem,
                    isClientDetail && classes.sessionListItemCompact,
                    session.id === selectedSessionId &&
                      classes.sessionListItemSelected
                  )}
                >
                  <div
                    className={clsx(
                      classes.sessionRow,
                      isClientDetail && classes.sessionRowCompact
                    )}
                  >
                    <div className={classes.sessionCopy}>
                      <Typography
                        variant="body1"
                        className={clsx(
                          classes.sessionTitle,
                          isClientDetail && classes.sessionTitleCompact
                        )}
                        title={
                          isClientDetail
                            ? formatDateTime(session.createdAt)
                            : undefined
                        }
                      >
                        {title}
                      </Typography>
                      {!isClientDetail ? (
                        <Typography
                          variant="body2"
                          className={classes.sessionMeta}
                        >
                          {formatDateTime(session.createdAt)}
                        </Typography>
                      ) : null}
                      {!isClientDetail &&
                      typeof session.overallScore === "number" ? (
                        <Typography
                          variant="body2"
                          className={classes.scoreText}
                        >
                          Score {Math.round(session.overallScore)}
                        </Typography>
                      ) : null}
                      {errorText ? (
                        <Tooltip title={errorText} placement="bottom-start">
                          <Typography
                            variant="body2"
                            className={clsx(
                              classes.sessionError,
                              isClientDetail && classes.sessionErrorCompact
                            )}
                          >
                            {isClientDetail ? "Couldn’t process" : errorText}
                          </Typography>
                        </Tooltip>
                      ) : null}
                    </div>
                    <div className={classes.sessionActions}>
                      {statusDots}
                      {!summary && showProcessingChip ? (
                        <TranscriptStatusChip status={session.status} />
                      ) : null}
                      <IconButton
                        size="small"
                        aria-label="Session actions"
                        aria-haspopup="menu"
                        aria-controls={
                          menuSessionId === session.id
                            ? "session-row-menu"
                            : undefined
                        }
                        onClick={(event) => openMenu(event, session.id)}
                        className={classes.moreButton}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </ListItem>
              </div>
            )
          })}
        </List>
      )}

      {deleteMenu}
    </>
  )

  if (isClientDetail) {
    return <ViraCard sx={{ maxWidth: "100%" }}>{body}</ViraCard>
  }

  return (
    <Paper className={classes.listPanel} elevation={0}>
      {body}
    </Paper>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    sectionTitle: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    listPanel: {
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      borderRadius: 4,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      overflow: "hidden",
    },
    listHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(2, 3),
      borderBottom: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      gap: theme.spacing(1),
    },
    listHeaderClientDetail: {
      borderBottom: "1px solid var(--vira-border, #E4E7EF)",
    },
    listHeaderTitle: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      minWidth: 0,
    },
    listHeaderActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    rail: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1),
      minHeight: 120,
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        minHeight: 0,
      },
    },
    railToolbar: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
      flexShrink: 0,
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(0.75),
      },
    },
    railHeader: {
      display: "flex",
      justifyContent: "center",
      paddingBottom: theme.spacing(0.5),
      [theme.breakpoints.down("md")]: {
        paddingBottom: 0,
      },
    },
    railCreateWrap: {
      display: "block",
      width: "100%",
      [theme.breakpoints.down("md")]: {
        width: 44,
        height: 44,
        flexShrink: 0,
      },
    },
    railCreateButton: {
      minHeight: 36,
      minWidth: 36,
      width: "100%",
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      fontSize: 16,
      fontWeight: 700,
      [theme.breakpoints.down("md")]: {
        minHeight: "44px !important",
        height: "44px !important",
        maxHeight: 44,
        minWidth: "44px !important",
        width: "44px !important",
        padding: "0 !important",
        fontSize: 18,
        lineHeight: 1,
        boxSizing: "border-box",
      },
    },
    railLoading: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(0.5, 0),
      [theme.breakpoints.down("md")]: {
        padding: 0,
        flexShrink: 0,
      },
    },
    railPager: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minWidth: 0,
      minHeight: 0,
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing(0.25),
      },
    },
    railPageButton: {
      display: "none",
      [theme.breakpoints.down("md")]: {
        display: "inline-flex",
        flexShrink: 0,
        padding: 4,
        color: "var(--vira-muted, #9AA3B5)",
        "&.Mui-disabled": {
          opacity: 0.35,
        },
      },
    },
    railList: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
      maxHeight: "min(60vh, 520px)",
      overflowY: "auto",
      overscrollBehavior: "contain",
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        alignItems: "stretch",
        gap: theme.spacing(0.75),
        flex: "1 1 auto",
        minWidth: 0,
        maxHeight: "none",
        overflowX: "auto",
        overflowY: "hidden",
        touchAction: "pan-x",
        WebkitOverflowScrolling: "touch",
        scrollSnapType: "x mandatory",
        scrollPaddingInline: theme.spacing(0.5),
        // Hide scrollbar but keep swipe/scroll.
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      },
    },
    railItem: {
      appearance: "none",
      border: "1px solid var(--vira-border, #E4E7EF)",
      backgroundColor: "var(--vira-soft-surface, #F1F2F6)",
      color: "var(--vira-ink, #1E263C)",
      borderRadius: 6,
      minHeight: 40,
      padding: theme.spacing(0.75, 0.5),
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background-color 120ms ease, border-color 120ms ease",
      "&:hover": {
        backgroundColor: "var(--vira-nav-active, rgba(117, 145, 228, 0.14))",
      },
      [theme.breakpoints.down("md")]: {
        flex: "0 0 auto",
        minWidth: 72,
        maxWidth: 96,
        minHeight: 44,
        padding: theme.spacing(1, 1.25),
        scrollSnapAlign: "start",
      },
    },
    railItemSelected: {
      backgroundColor: "var(--vira-nav-active, rgba(117, 145, 228, 0.22))",
      borderColor: "var(--vira-lavender-text, #5B6FD8)",
      boxShadow: "inset 3px 0 0 var(--vira-lavender-text, #5B6FD8)",
      [theme.breakpoints.down("md")]: {
        boxShadow: "inset 0 -3px 0 var(--vira-lavender-text, #5B6FD8)",
      },
    },
    railItemDate: {
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1.2,
      textAlign: "center",
      [theme.breakpoints.down("md")]: {
        fontSize: 12,
        whiteSpace: "nowrap",
      },
    },
    sessionListItem: {
      padding: theme.spacing(2, 3),
      alignItems: "flex-start",
      borderLeft: "3px solid transparent",
      transition: "background-color 120ms ease, border-color 120ms ease",
      "&:hover": {
        backgroundColor: "var(--vira-soft-surface, rgba(117, 145, 228, 0.06))",
      },
      "&.Mui-selected": {
        backgroundColor: "var(--vira-nav-active, rgba(117, 145, 228, 0.18))",
      },
      "&.Mui-selected:hover": {
        backgroundColor: "var(--vira-nav-active, rgba(117, 145, 228, 0.22))",
      },
    },
    sessionListItemCompact: {
      padding: theme.spacing(1.25, 2),
      alignItems: "center",
    },
    sessionListItemSelected: {
      borderLeftColor: "var(--vira-lavender-text, #5B6FD8)",
      boxShadow: "inset 0 0 0 1px var(--vira-border, #E4E7EF)",
    },
    sessionRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      width: "100%",
    },
    sessionRowCompact: {
      alignItems: "center",
      gap: theme.spacing(1),
    },
    sessionCopy: {
      minWidth: 0,
      flex: 1,
    },
    sessionActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
      flexShrink: 0,
    },
    moreButton: {
      padding: 4,
      color: "var(--vira-muted, #5C6578)",
    },
    sessionTitle: {
      fontWeight: 700,
      color: "var(--vira-ink, #1E263C)",
    },
    sessionTitleCompact: {
      fontSize: 13,
      lineHeight: 1.35,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    sessionMeta: {
      color: "var(--vira-muted, #5C6578)",
    },
    scoreText: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    statusStack: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 3,
      minWidth: 108,
      paddingTop: 2,
    },
    statusDots: {
      display: "flex",
      alignItems: "center",
      gap: 5,
      paddingRight: 2,
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    statusDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      boxSizing: "border-box",
      flexShrink: 0,
    },
    statusDotAlone: {
      width: 8,
      height: 8,
      display: "inline-block",
    },
    statusDotButton: {
      appearance: "none",
      backgroundColor: "transparent",
      padding: 0,
      margin: 0,
      cursor: "default",
      borderRadius: "50%",
      flexShrink: 0,
      boxSizing: "border-box",
      "&:focus": {
        outline: "2px solid var(--vira-lavender-text, #5B6FD8)",
        outlineOffset: 2,
      },
    },
    statusLabel: {
      color: "var(--vira-muted, #5C6578)",
      fontSize: 11,
      lineHeight: 1.2,
      fontWeight: 500,
    },
    statusLabelReady: {
      color: "var(--vira-ink, #1E263C)",
    },
    statusLabelInProgress: {
      color: colors.tertiary.tangerine,
    },
    dot_ready: {
      backgroundColor: colors.graphs.alerts.positive,
    },
    dot_in_progress: {
      backgroundColor: colors.tertiary.tangerine,
    },
    dot_missing: {
      backgroundColor: "transparent",
      border: `1.5px solid var(--vira-subtle, ${colors.neutral.cement})`,
    },
    dot_disabled: {
      backgroundColor: `var(--vira-border, ${colors.neutral.clay})`,
    },
    sessionError: {
      color: colors.secondary.crimson,
      marginTop: theme.spacing(0.5),
    },
    sessionErrorCompact: {
      marginTop: 2,
      fontSize: 11,
      lineHeight: 1.2,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    emptyState: {
      color: "var(--vira-muted, #5C6578)",
      padding: theme.spacing(2, 3),
    },
  })
)
